import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import SEO from '@/components/SEO';
import axios from 'axios';
import { ChevronRight, ChevronLeft, Phone, Truck, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      let response;
      // Try by slug first, then by ID
      try {
        response = await axios.get(`${API_URL}/api/products/by-slug/${id}`);
      } catch {
        response = await axios.get(`${API_URL}/api/products/${id}`);
      }
      setProduct(response.data);
      
      // Fetch related products
      const relatedResponse = await axios.get(
        `${API_URL}/api/products?category=${response.data.category_slug}&active_only=true`
      );
      setRelatedProducts(
        relatedResponse.data.filter(p => p.id !== response.data.id).slice(0, 4)
      );
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return null;
    return new Intl.NumberFormat('ro-RO', {
      style: 'currency',
      currency: 'RON',
      minimumFractionDigits: 2
    }).format(price);
  };

  const getImageUrl = (img) => {
    if (!img) return 'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=800';
    return img.startsWith('/api') ? `${API_URL}${img}` : img;
  };

  if (loading) {
    return (
      <Layout>
        <div className="pt-32 pb-20 bg-[#050505] min-h-screen">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="aspect-square bg-[#121212] animate-pulse" />
              <div className="space-y-6">
                <div className="h-8 bg-[#121212] animate-pulse w-1/3" />
                <div className="h-12 bg-[#121212] animate-pulse w-2/3" />
                <div className="h-24 bg-[#121212] animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="pt-32 pb-20 bg-[#050505] min-h-screen">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h1 className="text-white font-serif text-3xl mb-4">Produsul nu a fost găsit</h1>
            <Link to="/" className="text-[#D4AF37] hover:underline">
              Înapoi la pagina principală
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const images = product.images?.length > 0 
    ? product.images 
    : ['https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=800'];

  const productSlug = product.custom_slug || product.id;
  const firstImgUrl = images[0]?.startsWith('/api') ? `${API_URL}${images[0]}` : images[0];
  const seoTitle = product.seo_title || `${product.name} - ${product.code}`;
  const seoDesc = product.meta_description || `${product.name}. ${product.description || 'Descoperă acest produs premium la Shagun Surfaces.'}`.slice(0, 155);
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description || seoDesc,
    "sku": product.code,
    "image": firstImgUrl,
    "brand": { "@type": "Brand", "name": "Shagun Surfaces" },
    "offers": {
      "@type": "Offer",
      "url": `https://shagunsurfaces.ro/produs/${productSlug}`,
      "priceCurrency": "RON",
      "price": product.discounted_price || product.price || 0,
      "availability": "https://schema.org/InStock",
      "seller": { "@type": "Organization", "name": "Shagun Surfaces" }
    }
  };

  return (
    <Layout>
      <SEO
        title={seoTitle}
        description={seoDesc}
        canonicalUrl={`https://shagunsurfaces.ro/produs/${productSlug}`}
        image={firstImgUrl}
        type="product"
        jsonLd={productSchema}
      />
      <div className="pt-32 pb-20 bg-[#050505]" data-testid="product-detail-page">
        <div className="max-w-7xl mx-auto px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-8">
            <Link to="/" className="text-[#A3A3A3] hover:text-[#D4AF37]">Acasă</Link>
            <ChevronRight size={14} className="text-[#A3A3A3]" />
            <Link 
              to={`/${product.category_slug}`} 
              className="text-[#A3A3A3] hover:text-[#D4AF37] capitalize"
            >
              {product.category_slug.replace('-', ' ')}
            </Link>
            <ChevronRight size={14} className="text-[#A3A3A3]" />
            <span className="text-[#D4AF37]">{product.name}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Images */}
            <div>
              {/* Main Image */}
              <div className="aspect-square bg-[#121212] overflow-hidden mb-4 relative">
                {product.badge && (
                  <span className={`absolute top-4 left-4 z-10 text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1 ${
                    product.badge === 'new' ? 'badge-new' : 
                    product.badge === 'offer' ? 'badge-offer' : 'badge-bestseller'
                  }`}>
                    {product.badge === 'new' ? 'Nou' : product.badge === 'offer' ? 'Ofertă' : 'Bestseller'}
                  </span>
                )}
                <img
                  src={getImageUrl(images[selectedImage])}
                  alt={product.alt_texts?.[selectedImage] || `${product.name} - ${product.code}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=800';
                  }}
                />
                
                {/* Navigation Arrows */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={() => setSelectedImage(prev => prev === 0 ? images.length - 1 : prev - 1)}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 text-white flex items-center justify-center hover:bg-[#D4AF37] transition-colors"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      onClick={() => setSelectedImage(prev => prev === images.length - 1 ? 0 : prev + 1)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 text-white flex items-center justify-center hover:bg-[#D4AF37] transition-colors"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="flex gap-4 overflow-x-auto pb-2">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`flex-shrink-0 w-20 h-20 overflow-hidden ${
                        selectedImage === idx ? 'ring-2 ring-[#D4AF37]' : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={getImageUrl(img)}
                        alt={`${product.name} ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=200';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div>
              <p className="text-[#D4AF37] text-sm tracking-widest mb-2">{product.code}</p>
              <h1 className="font-serif text-3xl sm:text-4xl text-white mb-6">{product.name}</h1>

              {/* Price */}
              {product.price && (
                <div className="flex items-center gap-4 mb-6">
                  {product.discounted_price ? (
                    <>
                      <span className="text-[#D4AF37] font-bold text-3xl">
                        {formatPrice(product.discounted_price)}
                      </span>
                      <span className="text-[#A3A3A3] line-through text-xl">
                        {formatPrice(product.price)}
                      </span>
                      <span className="bg-red-500 text-white text-sm px-3 py-1 font-bold">
                        -{product.discount_percent}%
                      </span>
                    </>
                  ) : (
                    <span className="text-[#D4AF37] font-bold text-3xl">
                      {formatPrice(product.price)}
                    </span>
                  )}
                </div>
              )}

              {/* Description */}
              {product.description && (
                <p className="text-[#A3A3A3] leading-relaxed mb-8">
                  {product.description}
                </p>
              )}

              {/* Technical Specs */}
              {product.technical_specs?.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-white font-serif text-xl mb-4">Specificații Tehnice</h3>
                  <div className="bg-[#121212] border border-white/10">
                    {product.technical_specs.map((spec, idx) => (
                      <div 
                        key={idx}
                        className={`flex justify-between py-3 px-4 ${
                          idx !== product.technical_specs.length - 1 ? 'border-b border-white/5' : ''
                        }`}
                      >
                        <span className="text-[#A3A3A3]">{spec.name}</span>
                        <span className="text-white font-medium">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a
                  href={`https://wa.me/40761961789?text=Bună ziua! Sunt interesat de produsul: ${product.name} (${product.code})`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary flex items-center justify-center gap-2"
                  data-testid="product-whatsapp-btn"
                >
                  <Phone size={18} />
                  Contactează-ne
                </a>
              </div>

              {/* Benefits */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-[#121212] border border-white/5">
                  <Truck className="text-[#D4AF37]" size={24} />
                  <div>
                    <p className="text-white text-sm font-medium">Livrare Națională</p>
                    <p className="text-[#A3A3A3] text-xs">În toată România</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-[#121212] border border-white/5">
                  <Shield className="text-[#D4AF37]" size={24} />
                  <div>
                    <p className="text-white text-sm font-medium">Calitate Garantată</p>
                    <p className="text-[#A3A3A3] text-xs">Produse premium</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-20">
              <h2 className="font-serif text-2xl text-white mb-8">Produse Similare</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {relatedProducts.map((p) => (
                  <Link
                    key={p.id}
                    to={`/produs/${p.custom_slug || p.id}`}
                    className="bg-[#121212] border border-white/5 overflow-hidden group hover:border-[#D4AF37]/30 transition-all"
                  >
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={getImageUrl(p.images?.[0])}
                        alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=400';
                        }}
                      />
                    </div>
                    <div className="p-4">
                      <p className="text-[#D4AF37] text-xs mb-1">{p.code}</p>
                      <h3 className="text-white font-serif line-clamp-1">{p.name}</h3>
                      {p.price && (
                        <p className="text-[#D4AF37] font-semibold mt-2">
                          {formatPrice(p.discounted_price || p.price)}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ProductDetailPage;
